import {Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, TemplateRef} from '@angular/core';
import {UtilityService} from '@irb/irb-common/services/utility.service';
import {IrbSettingsService} from '@irb/irb-settings/irb-settings.service';
import {AppService} from '@irb/app.service';
import {IEvent, IrbAppEvents, IrbSettingsEvents} from '@irb-common/events';
import {Subscription, Observable, of} from 'rxjs';
import {MMCSettingsPage} from "@irb/irb-settings/irb-mmc-settings.page";
import {IrbMMCSettingsService} from "@irb/irb-settings/settings-mmc/irb-mmc-settings.service";
import {IUserDetails} from "@irb/app.model";
import { DatePipe } from '@angular/common';

@Component({ standalone: false,
    selector: 'irb-settings-mmc',
    templateUrl: './settings-mmc.component.html',
    styleUrls: ['./settings-mmc.component.scss', '../../irb-common/styles/dialog.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SettingsMmcComponent implements MMCSettingsPage<any>, OnInit, OnDestroy {
    defaultExpansionPanels: any[] = ['MODEL_MARKET_CENTER_ASSET_CLASS', 'MODEL_MARKET_CENTER_RISK_TOLERANCE', 'MODEL_MARKET_CENTER_ASSET_TYPE', 'MODEL_MARKET_CENTER_BILLING_DEFAULTS', 'MODEL_MARKET_CENTER_USER_AGREEMENT', 'MODEL_MARKET_CENTER_CUSTOM_DISCLOSURE', 'MODEL_MARKET_CENTER_REPORTING_INVOICE'];
    _modelMarketSettings: any;
    originalModelMarketSettings: any;
    _isModified: boolean;
    eventSubscription: Subscription;
    showUnsavedChangesWarning: boolean = false;
    validationErrors: string[];
    validationErrorTemplates: TemplateRef<any>[];
    newAgreementFlag = false;
    showResetAgreementBanner = false;
    lastResetDate: string = '';
    customModelDisclosureDoc: any;
    customModelDisclosureDocContent: any;

    constructor(
        public iss: IrbSettingsService,
        public mmcSettingService: IrbMMCSettingsService,
        private cdr: ChangeDetectorRef,
        private util: UtilityService,
        private datePipe: DatePipe,
        private as: AppService) {
        this.eventSubscription = this.as.getEvents().subscribe((s) => {
            this.processEvent(s);
        });
    }

    ngOnInit() {
        this.mmcSettingService.getMMCTCInfo().subscribe((mmcTCInfo: any) => {
            if (mmcTCInfo.mmcTCResetDate) {
                const resetData = new Date(mmcTCInfo.mmcTCResetDate);
                this.lastResetDate = this.datePipe.transform(resetData, 'hh:mm a , MM/dd/yyyy').toLowerCase().replace(',', 'ET,');
            }
        });

        this.mmcSettingService.getModelMarketSettings().subscribe((data: any) => {
            this.load(data);
            this.loadMdlDisclosureText();
        });
        this.iss.loadExpansionState(this.defaultExpansionPanels.length, "settings-mmc", this.defaultExpansionPanels);
        this.cdr.detectChanges();
    }

    ngOnDestroy() {
        this.eventSubscription.unsubscribe();
    }

    onInputChange(): any {
        window.setTimeout(() => {
            this.isModified = !this.util.compare(this.originalModelMarketSettings, this.modelMarketSettings);
            this.as.fireEvent((this.isModified) ? IrbSettingsEvents.IS_MODIFIED : IrbSettingsEvents.IS_NOT_MODIFIED);
            this.validateInput().subscribe(_validationErrors => {});
            this.cdr.detectChanges();
        });
    }

    validateInput(): Observable<string[]> {
        this.validationErrors = [];
        this.validationErrorTemplates = [];
        return of(this.validationErrors);
    }

    load(data: any): void {
        this.modelMarketSettings = data;
        this.originalModelMarketSettings = this.util.clone(this.modelMarketSettings);
        this.isModified = false;
        this.showUnsavedChangesWarning = false;
        this.as.fireEvent(IrbSettingsEvents.IS_NOT_MODIFIED);
        this.cdr.detectChanges();
    }

    save(): void {
        this.validateInput().subscribe((validationErrors) => {
            if (validationErrors.length === 0) {
                this.as.fireEvent(IrbAppEvents.SHOW_WAIT);
                let objectToSave = this.getSaveMMCObject();
                this.mmcSettingService.saveMMCSetting(objectToSave).subscribe((response: any) => {
                    if (response.success) {
                        this.as.fireEvent(IrbSettingsEvents.SAVE_SUCCESS, "Model Market Center settings were updated.");
                        this.load(response);
                    } else {
                        let errorMessages = response.errorMessages;
                        if (errorMessages && errorMessages.length > 0) {
                            errorMessages.forEach((errorMessage: any) => {
                                this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                                    severity: 'error',
                                    summary: '',
                                    detail: errorMessage
                                });
                            });
                        }
                    }
                    // load irrespective of old or new data.
                    this.mmcSettingService.getModelMarketSettings().subscribe((data: any) => {
                        this.load(data);
                    },()=>{},()=>{
                        this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
                    });
                }, () => {
                    this.as.fireEvent(IrbSettingsEvents.SAVE_ERROR, "An error occurred sending your request.");
                    this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
                }, () => {

                });
            }
        });

    }

    processEvent(event: IEvent) {
        switch (event.type) {
            case IrbSettingsEvents.SAVE:
                this.save();
                break;
            case IrbSettingsEvents.UNSAVED_CHANGES:
                if (this.isModified) {
                    this.showUnsavedChangesWarning = true;
                    this.cdr.detectChanges();
                }
                break;
            case IrbSettingsEvents.REVERT_CHANGES:
                this.revert();
                break;
        }
    }

    revert(): void {
        this.load(this.originalModelMarketSettings);
    }

    get modelMarketSettings() {
        return this._modelMarketSettings;
    }

    set modelMarketSettings(newModelMarketSettings: any) {
        this._modelMarketSettings = newModelMarketSettings;
    }

    get isModified() {
        return this._isModified;
    }

    set isModified(newIsModified: boolean) {
        this._isModified = newIsModified;
    }

    onAssetClassChanged(assetClasses: any[]) {
        this.modelMarketSettings.data.modelMarketGlobalSettings.ASSET_CLASS = assetClasses;
        this.onInputChange();
    }

    onAssetTypeChanged(assetTypes: any[]) {
        this.modelMarketSettings.data.modelMarketGlobalSettings.ASSET_TYPE = assetTypes;
        this.onInputChange();
    }

    onRiskTypeChanged(assetTypes: any[]) {
        this.modelMarketSettings.data.modelMarketGlobalSettings.RISK_TOLERANCE = assetTypes;
        this.onInputChange();
    }
    onMmcNerTierListChanged(mmcNERTierList: any[]) {
        this.modelMarketSettings.data.mmcNerTierList = mmcNERTierList;
        this.onInputChange();
    }

    getSaveMMCObject() {
        let objectToSave = {
            "modelMarketGlobalSettings": {
                "ASSET_CLASS": this.modelMarketSettings.data.modelMarketGlobalSettings.ASSET_CLASS,
                "RISK_TOLERANCE": this.modelMarketSettings.data.modelMarketGlobalSettings.RISK_TOLERANCE,
                "ASSET_TYPE": this.modelMarketSettings.data.modelMarketGlobalSettings.ASSET_TYPE
            },
            "mmcNerTierList": this.modelMarketSettings.data.mmcNerTierList,
            "customModelDisclosure": this.modelMarketSettings.data.customModelDisclosure
        };

        return objectToSave;
    }

    resetUserAgreementClick() {
        this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
            dWidth: 530,
            dType: 'NONE',
            dTitle: 'Reset Model Market Center User Agreement?',
            msg: 'All Model Market Center users will be required to accept a new agreement.',
            dCancelBtnLabel: "No, Go Back",
            dProceedBtnLabel: "Yes, Reset",
            onCancel: (() => {
            }),
            onProceed: (() => {
                this.proceedWithResetUserAgreement()
            })
        });
    }
    proceedWithResetUserAgreement() {
        this.as.fireEvent(IrbAppEvents.SHOW_WAIT);
        this.mmcSettingService.resetUserAgreement().subscribe(() => {
            this.showResetAgreementBanner = true;
            this.mmcSettingService.getMMCTCInfo().subscribe((mmcTCInfo: any) => {
                if (mmcTCInfo.mmcTCResetDate) {
                    const resetData = new Date(mmcTCInfo.mmcTCResetDate);
                    this.lastResetDate = this.datePipe.transform(resetData, 'hh:mm a , MM/dd/yyyy').toLowerCase().replace(',', 'ET,');
                }
                this.cdr.detectChanges();
                this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
            }, () => {
                this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
            });
        }, () => {
            this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
        });

    }


    loadMdlDisclosureText() {
        const doc: any = document.getElementById("customMdlDisclosureText");
        if (doc) {
            this.customModelDisclosureDoc = doc.contentDocument;
            this.customModelDisclosureDoc.designMode = "On";
            this.customModelDisclosureDocContent = this.customModelDisclosureDoc.body;
            this.customModelDisclosureDocContent.style.fontFamily = "sans-serif";
            this.customModelDisclosureDocContent.style.fontSize = "11px";
            this.customModelDisclosureDocContent.style.fontWeight = "normal";
            this.customModelDisclosureDocContent.style.overflowX = "hidden";
            this.customModelDisclosureDocContent.innerHTML = this.modelMarketSettings?.data?.customModelDisclosure || '';

            this.customModelDisclosureDocContent.addEventListener('keyup', () => {
               this.updateDataAfterCustomModelChange();
            }, false);
        }
    }

    cleanDocMdl() {
        this.customModelDisclosureDocContent.innerHTML = '';
        this.customModelDisclosureDocContent.focus();
        this.updateDataAfterCustomModelChange();
    }

    formatDocMdl(command: any) {
        if (this.customModelDisclosureDoc) {
            this.customModelDisclosureDoc.execCommand(command);
            this.updateDataAfterCustomModelChange();
        }
    }

    updateDataAfterCustomModelChange() {
        let d = this.customModelDisclosureDocContent.innerHTML.trim();
        this.modelMarketSettings.data.customModelDisclosure = d;
        this.onInputChange();
    }

    addLinkMdl() {
        let sLnk = window.prompt('Enter the URL here','http:\/\/');
        if (sLnk && sLnk !== '' && sLnk !== 'http://') {
            if (this.customModelDisclosureDoc) {
                this.customModelDisclosureDoc.execCommand("CreateLink", false, sLnk);
                this.updateDataAfterCustomModelChange();
            }
        }
    }

    printDoc() {
      // const printedHtml = isDisclosure ? this.firmDisclosureDocContent.innerHTML : this.customModelDisclosureDocContent.innerHTML;
      const printedHtml = this.customModelDisclosureDocContent.innerHTML;
      let p = window.open("","_blank");
      if (p && p.document) {
        let doc = p.document;
        let headElements = doc.getElementsByTagName('head');
        let headElement = headElements[0];
        let titleElement = doc.createElement('title');
        titleElement.innerText = "Print";
        headElement.appendChild(titleElement);
        let bodyElements = doc.getElementsByTagName('body');
        let bodyElement = bodyElements[0];
        bodyElement.setAttribute('onload', 'print();');
        let wrapperElement = doc.createElement('div');
        let docFragment = doc.createRange().createContextualFragment(printedHtml);
        wrapperElement.appendChild(docFragment);
        let scriptElement = document.createElement('script');
        scriptElement.setAttribute('type', 'text/javascript');
        scriptElement.innerText = 'print();'
        bodyElement.appendChild(wrapperElement);
        bodyElement.appendChild(scriptElement);
      }
    }


}

